import { BusinessObjectBase } from './base';
import { imageSchema } from '@schemas/business-objects/image';
import type { Resource } from './resource';
import type { Id } from '../base-types';
import type { TextBox } from './text-box';
import type { User } from './user';

export interface ImageData {
  readonly id: Id;
  readonly name?: string;
  readonly fileName?: string;
  readonly mimeType?: string;
  readonly size?: number;
  readonly base64Content?: string;
  readonly fixedPosition?: boolean;
  readonly sequence?: number;
  readonly positionX?: number;
  readonly positionY?: number;
  readonly isBackground?: boolean;
  readonly resourceId: Id;
  readonly resource?: Resource;
  readonly locked?: boolean;
}

export interface Image extends Readonly<ImageData> {}
type ImageCreateWithChildrenData = Partial<ImageData>;

export class Image extends BusinessObjectBase {
  static readonly entityName = 'Image' as const;
  static readonly plural = 'images';
  static schema = imageSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<Image[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new Image(data));
  }

  static async findOne(filter?: any): Promise<Image | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new Image(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<Image> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new Image(data);
  }

  static async create(data: Omit<ImageData, 'id'>): Promise<Image> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new Image(result);
  }

  static async createMany(data: Omit<ImageData, 'id'>[]): Promise<Image[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new Image(item));
  }

  static async createWithChildren(
    data: ImageCreateWithChildrenData,
  ): Promise<Image> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new Image(result);
  }

  static async createManyWithChildren(
    data: ImageCreateWithChildrenData[],
  ): Promise<Image[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new Image(item));
  }

  static async updateById(id: Id, updates: Partial<ImageData>): Promise<Image> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new Image(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }

  // --- Behaviors ---

  async dataForCopy(): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${Image.plural}/${(this as any).id}/data-for-copy`;
    return BusinessObjectBase.post<any>(url, {});
  }
  async content(): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${Image.plural}/${(this as any).id}/content`;
    return BusinessObjectBase.get<any>(url);
  }
}
